import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Wallet, Heart, Lightbulb, ShieldCheck, PiggyBank, Sparkles } from 'lucide-react';

/**
 * BilanMensuel - Bloc Bilan avec ton humain et ludique
 * Affiche un résumé du mois avec des conseils personnalisés
 * Synchronisé avec le mois affiché dans le calendrier
 */
const BilanMensuel = ({ monthSummary, currency = '€', monthName, year }) => {
  if (!monthSummary) return null;

  const { total_credits, total_debits, final_balance } = monthSummary;
  const soldeNet = total_credits - total_debits;

  // Déterminer la situation financière
  const getSituation = () => {
    const ratio = total_credits > 0 ? (total_debits / total_credits) * 100 : 100;
    
    if (soldeNet >= 500) return 'excellent';
    if (soldeNet >= 100) return 'bon';
    if (soldeNet >= 0) return 'equilibre';
    if (soldeNet >= -100) return 'attention';
    return 'difficile';
  };

  const situation = getSituation();

  // Messages personnalisés selon la situation
  const getMessages = () => {
    switch (situation) {
      case 'excellent':
        return {
          titre: 'Félicitations, vous gérez remarquablement bien !',
          sousTitre: 'Votre budget est en excellente santé ce mois-ci.',
          conseil: 'C\'est le moment idéal pour renforcer votre épargne ou vous faire plaisir de manière raisonnable.',
          icon: <Sparkles className="h-6 w-6 text-emerald-500" />,
          bgGradient: 'from-emerald-50 to-teal-50',
          borderColor: 'border-emerald-200',
          accentColor: 'text-emerald-600'
        };
      case 'bon':
        return {
          titre: 'Bravo, vous êtes sur la bonne voie !',
          sousTitre: 'Votre budget est bien équilibré.',
          conseil: 'Continuez ainsi et pensez à garder une petite réserve pour les imprévus.',
          icon: <Heart className="h-6 w-6 text-teal-500" />,
          bgGradient: 'from-teal-50 to-cyan-50',
          borderColor: 'border-teal-200',
          accentColor: 'text-teal-600'
        };
      case 'equilibre':
        return {
          titre: 'Vous maintenez l\'équilibre',
          sousTitre: 'Votre budget est juste, mais stable.',
          conseil: 'Identifiez quelques petites économies possibles pour plus de sérénité.',
          icon: <ShieldCheck className="h-6 w-6 text-blue-500" />,
          bgGradient: 'from-blue-50 to-indigo-50',
          borderColor: 'border-blue-200',
          accentColor: 'text-blue-600'
        };
      case 'attention':
        return {
          titre: 'Budget un peu tendu ce mois-ci',
          sousTitre: 'Pas d\'inquiétude, cela arrive à tout le monde.',
          conseil: 'Analysez vos dépenses pour identifier où faire des ajustements. Chaque petit effort compte.',
          icon: <Lightbulb className="h-6 w-6 text-amber-500" />,
          bgGradient: 'from-amber-50 to-orange-50',
          borderColor: 'border-amber-200',
          accentColor: 'text-amber-600'
        };
      case 'difficile':
        return {
          titre: 'Mois difficile, mais vous n\'êtes pas seul',
          sousTitre: 'Le coût de la vie est élevé et cela touche beaucoup de personnes.',
          conseil: 'L\'important est de comprendre votre situation pour repartir sur de bonnes bases. Courage !',
          icon: <Heart className="h-6 w-6 text-rose-500" />,
          bgGradient: 'from-rose-50 to-pink-50',
          borderColor: 'border-rose-200',
          accentColor: 'text-rose-600'
        };
      default:
        return {
          titre: 'Votre bilan mensuel',
          sousTitre: '',
          conseil: '',
          icon: <Wallet className="h-6 w-6 text-gray-500" />,
          bgGradient: 'from-gray-50 to-slate-50',
          borderColor: 'border-gray-200',
          accentColor: 'text-gray-600'
        };
    }
  };

  const messages = getMessages();

  // Suggestions pour l'argent restant (si positif)
  const getSuggestions = () => {
    if (soldeNet <= 0) return null;
    
    return [
      { icon: <ShieldCheck className="h-4 w-4" />, text: 'Garder une réserve pour les imprévus', color: 'text-blue-600' },
      { icon: <PiggyBank className="h-4 w-4" />, text: 'Renforcer votre épargne', color: 'text-emerald-600' },
      { icon: <Sparkles className="h-4 w-4" />, text: 'Vous faire plaisir raisonnablement', color: 'text-amber-600' }
    ];
  };

  const suggestions = getSuggestions();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className={`rounded-2xl border-2 ${messages.borderColor} bg-gradient-to-br ${messages.bgGradient} overflow-hidden`}
      data-testid="bilan-mensuel"
    >
      {/* En-tête du bilan */}
      <div className="p-4 border-b border-white/50">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0`}>
            {messages.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold ${messages.accentColor} text-base`}>
              {messages.titre}
            </h3>
            <p className="text-sm text-gray-600 mt-0.5">
              {messages.sousTitre}
            </p>
          </div>
        </div>
      </div>

      {/* Corps du bilan */}
      <div className="p-4 space-y-4">
        {/* Question principale */}
        <div className="text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
            Où en êtes-vous en {monthName} {year} ?
          </p>
        </div>

        {/* Résumé financier */}
        <div className="grid grid-cols-3 gap-3">
          {/* Entrées */}
          <motion.div 
            className="bg-white/70 rounded-xl p-3 text-center"
            whileTap={{ scale: 0.98 }}
          >
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center mx-auto mb-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
            </div>
            <p className="text-lg font-bold text-emerald-600">
              +{total_credits.toFixed(0)}{currency}
            </p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Reçu</p>
          </motion.div>

          {/* Sorties */}
          <motion.div 
            className="bg-white/70 rounded-xl p-3 text-center"
            whileTap={{ scale: 0.98 }}
          >
            <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center mx-auto mb-2">
              <TrendingDown className="h-4 w-4 text-rose-500" />
            </div>
            <p className="text-lg font-bold text-rose-500">
              -{total_debits.toFixed(0)}{currency}
            </p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Utilisé</p>
          </motion.div>

          {/* Reste */}
          <motion.div 
            className={`rounded-xl p-3 text-center ${
              soldeNet >= 0 ? 'bg-teal-100/70' : 'bg-rose-100/70'
            }`}
            whileTap={{ scale: 0.98 }}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2 ${
              soldeNet >= 0 ? 'bg-teal-200' : 'bg-rose-200'
            }`}>
              <Wallet className={`h-4 w-4 ${soldeNet >= 0 ? 'text-teal-700' : 'text-rose-700'}`} />
            </div>
            <p className={`text-lg font-bold ${soldeNet >= 0 ? 'text-teal-700' : 'text-rose-700'}`}>
              {soldeNet >= 0 ? '+' : ''}{soldeNet.toFixed(0)}{currency}
            </p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Reste</p>
          </motion.div>
        </div>

        {/* Conseil personnalisé */}
        {messages.conseil && (
          <div className="bg-white/50 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700 leading-relaxed">
                {messages.conseil}
              </p>
            </div>
          </div>
        )}

        {/* Suggestions si budget positif */}
        {suggestions && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 text-center">
              Que faire avec ce qu'il vous reste ?
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {suggestions.map((suggestion, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-1.5 bg-white/70 rounded-full px-3 py-1.5 text-xs"
                >
                  <span className={suggestion.color}>{suggestion.icon}</span>
                  <span className="text-gray-600">{suggestion.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default BilanMensuel;
